import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UploadCloud, Loader2, FileImage, Eye, EyeOff, Download, RefreshCw,
  Layers, Crosshair, Pen, FileJson, AlertCircle,
} from 'lucide-react';
import { HudCard, HudButton } from '../components/hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /lab/floorplan-extractor — POC page for the deterministic OpenCV
 * wall-extraction pipeline. The user uploads a plan, the backend returns
 * the segments JSON + 4 base64 previews (binarized / wall mask / skeleton /
 * overlay). This is the foundation for the future Canvas Editor where the
 * user will manually validate/adjust the vectors before going to 3D.
 */
const FloorplanExtractorLab = () => {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [approxEps, setApproxEps] = useState(2.5);
  const [minAreaRatio, setMinAreaRatio] = useState(0.0008);
  const [showJson, setShowJson] = useState(false);
  const [activePreview, setActivePreview] = useState('overlay');
  const fileInputRef = useRef(null);

  const handlePick = (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type || '')) {
      toast.error('Format non supporté (PNG / JPG / WEBP)');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 20 Mo)');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setFilePreview(e.target.result);
    reader.readAsDataURL(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handlePick(e.dataTransfer?.files?.[0]);
  };

  const runExtraction = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('approx_eps', String(approxEps));
      form.append('min_area_ratio', String(minAreaRatio));
      form.append('include_previews', 'true');
      const r = await axios.post(`${API}/lab/floorplan/extract`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setResult(r.data);
      toast.success(`Extraction réussie : ${r.data.stats.segments} segments`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'extraction');
    } finally { setLoading(false); }
  }, [file, approxEps, minAreaRatio]);

  const downloadJson = () => {
    if (!result) return;
    const slim = {
      image: result.image,
      segments: result.segments,
      stats: result.stats,
      params: result.params,
    };
    const blob = new Blob([JSON.stringify(slim, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `floorplan-geometry-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--c-bg-deep)' }} data-testid="floorplan-extractor-lab">
      <div className="max-w-[1600px] mx-auto p-6 lg:p-10">
        <header className="mb-6">
          <span className="hud-eyebrow hud-eyebrow-gold">Sprint Core Engine · POC</span>
          <h1 className="mt-2 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 32 }}>
            Floorplan → Vectors
          </h1>
          <p className="mt-2 text-[13px] max-w-3xl" style={{ color: 'var(--c-text-muted)' }}>
            Pipeline déterministe d'extraction des murs depuis une image de plan.
            Aucune hallucination — uniquement OpenCV + morphologie + squelettisation
            + approxPolyDP. Le JSON de sortie nourrira le Canvas Editor (Fabric.js).
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
          {/* ===== Left column : Upload + params + raw JSON ===== */}
          <div className="space-y-4">
            <HudCard variant="glass" bracket data-testid="extractor-upload-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <span className="hud-eyebrow hud-eyebrow-gold">1. Plan source</span>
                  <p className="mt-1 font-semibold" style={{ color: 'var(--c-text)', fontSize: 14 }}>Upload</p>
                </div>
                <FileImage className="w-4 h-4 shrink-0" style={{ color: 'var(--c-gold)' }} />
              </div>
              <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                className="block rounded-lg px-5 py-8 flex flex-col items-center text-center cursor-pointer transition-all"
                style={{
                  background: filePreview ? 'transparent' : 'rgba(255,255,255,0.03)',
                  border: `1.5px dashed ${filePreview ? 'rgba(217,160,91,0.45)' : 'rgba(255,255,255,0.18)'}`,
                }}
                data-testid="extractor-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => { handlePick(e.target.files?.[0]); e.target.value = ''; }}
                  data-testid="extractor-file-input"
                />
                {filePreview ? (
                  <img src={filePreview} alt="preview" className="max-h-64 rounded shadow-lg" />
                ) : (
                  <>
                    <UploadCloud className="w-10 h-10 mb-2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <p className="text-[12.5px] font-semibold" style={{ color: 'var(--c-text)' }}>
                      Glissez votre plan ici
                    </p>
                    <p className="text-[11px] mt-1" style={{ color: 'var(--c-text-muted)' }}>
                      PNG / JPG / WEBP — max 20 Mo
                    </p>
                  </>
                )}
              </label>
              {file && (
                <p className="text-[11px] mt-2" style={{ color: 'var(--c-text-muted)' }}>
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </HudCard>

            <HudCard variant="glass" bracket data-testid="extractor-params-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <span className="hud-eyebrow hud-eyebrow-gold">2. Paramètres</span>
                  <p className="mt-1 font-semibold" style={{ color: 'var(--c-text)', fontSize: 14 }}>Ajustements fins</p>
                </div>
                <Crosshair className="w-4 h-4 shrink-0" style={{ color: 'var(--c-gold)' }} />
              </div>
              <div className="space-y-4">
                <Slider
                  label="Simplification (approxPolyDP ε)"
                  min={0.5} max={20} step={0.5}
                  value={approxEps} onChange={setApproxEps}
                  hint="Plus haut = moins de points, lignes plus droites"
                  testid="extractor-approx-eps"
                />
                <Slider
                  label="Surface min. (× total)"
                  min={0.0001} max={0.01} step={0.0001}
                  value={minAreaRatio} onChange={setMinAreaRatio}
                  fmt={(v) => `${(v * 100).toFixed(2)} %`}
                  hint="Filtre le texte / mobilier / annotations"
                  testid="extractor-min-area"
                />
                <button
                  type="button"
                  onClick={runExtraction}
                  disabled={!file || loading}
                  data-testid="extractor-run-btn"
                  className="w-full px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-semibold transition-all hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
                  style={{
                    background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
                    color: '#0E0E10',
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '13px',
                    boxShadow: '0 4px 14px -4px rgba(217,160,91,0.45)',
                  }}
                >
                  {loading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Pen className="w-4 h-4" strokeWidth={2.5} />}
                  Extraire la géométrie
                </button>
              </div>
            </HudCard>

            {result && (
              <HudCard variant="glass" bracket data-testid="extractor-stats-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="hud-eyebrow hud-eyebrow-gold">3. Statistiques</span>
                    <p className="mt-1 font-semibold" style={{ color: 'var(--c-text)', fontSize: 14 }}>
                      {result.stats.segments} segments extraits
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowJson((v) => !v)}
                      data-testid="extractor-toggle-json"
                      className="text-[11px] px-2 py-1 rounded flex items-center gap-1.5 hover:bg-white/[0.06] transition-colors"
                      style={{ color: 'var(--c-text-2)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      {showJson ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      JSON
                    </button>
                    <button
                      type="button"
                      onClick={downloadJson}
                      data-testid="extractor-download-json"
                      className="text-[11px] px-2 py-1 rounded flex items-center gap-1.5 hover:bg-white/[0.06] transition-colors"
                      style={{ color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.35)' }}
                    >
                      <Download className="w-3 h-3" /> .json
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[11.5px]">
                  <StatPill label="Mur porteur" value={result.stats.by_class.wall_load_bearing} tone="#FCA5A5" />
                  <StatPill label="Mur" value={result.stats.by_class.wall} tone="#FBBF24" />
                  <StatPill label="Cloison" value={result.stats.by_class.partition} tone="#A0BFC0" />
                  <StatPill label="Longueur ∑" value={`${result.stats.total_length_px.toFixed(0)} px`} tone="rgba(255,255,255,0.65)" />
                </div>
                {showJson && (
                  <pre
                    className="mt-3 p-3 rounded text-[10px] font-mono max-h-[400px] overflow-auto"
                    style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.10)', color: 'var(--c-text-2)' }}
                    data-testid="extractor-json-view"
                  >
                    {JSON.stringify({
                      image: result.image,
                      stats: result.stats,
                      params: result.params,
                      segments: result.segments,
                    }, null, 2)}
                  </pre>
                )}
              </HudCard>
            )}
          </div>

          {/* ===== Right column : Pipeline previews ===== */}
          <HudCard variant="glass" bracket data-testid="extractor-previews-card" className="min-h-[600px]">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <span className="hud-eyebrow hud-eyebrow-gold">Pipeline visuel</span>
                <p className="mt-1 font-semibold" style={{ color: 'var(--c-text)', fontSize: 14 }}>
                  Étapes du pipeline OpenCV
                </p>
              </div>
              <Layers className="w-4 h-4 shrink-0" style={{ color: 'var(--c-gold)' }} />
            </div>

            {result ? (
              <>
                <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="extractor-preview-tabs">
                  {[
                    ['overlay', 'Vecteurs', '#D9A05B'],
                    ['binarized_b64', 'Binarisation', '#94A3B8'],
                    ['wall_mask_b64', 'Masque murs', '#60A5FA'],
                    ['skeleton_b64', 'Squelette', '#A78BFA'],
                  ].map(([key, label, tone]) => {
                    const tabKey = key === 'overlay' ? 'overlay_b64' : key;
                    const active = activePreview === (key === 'overlay' ? 'overlay' : key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActivePreview(key === 'overlay' ? 'overlay' : key)}
                        data-testid={`extractor-tab-${key}`}
                        className="text-[11px] px-2.5 py-1 rounded transition-colors"
                        style={{
                          background: active ? 'rgba(217,160,91,0.12)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? 'rgba(217,160,91,0.45)' : 'rgba(255,255,255,0.10)'}`,
                          color: active ? 'var(--c-gold)' : tone,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div
                  className="rounded-lg overflow-auto"
                  style={{
                    background: '#0A0A0C',
                    border: '1px solid rgba(255,255,255,0.10)',
                    maxHeight: '70vh',
                  }}
                >
                  <img
                    src={result.previews[activePreview === 'overlay' ? 'overlay_b64' : activePreview]}
                    alt={activePreview}
                    className="w-full h-auto block"
                    data-testid={`extractor-preview-img-${activePreview}`}
                  />
                </div>
                <p className="mt-2 text-[10.5px]" style={{ color: 'var(--c-text-faint)' }}>
                  {{
                    overlay: 'Vecteurs détectés (rouge = porteur, ambre = mur, gris = cloison) avec sommets en cyan.',
                    binarized_b64: 'Binarisation adaptative (Gaussian) après égalisation CLAHE.',
                    wall_mask_b64: 'Connected components filtrés (surface + ratio d\'allongement) puis closing.',
                    skeleton_b64: 'Squelette médian (Zhang-Suen, skimage.morphology.skeletonize) — épaisseur 1 px.',
                  }[activePreview]}
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center"
                style={{ color: 'var(--c-text-muted)' }}>
                <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'rgba(255,255,255,0.30)' }} />
                <p className="text-[12.5px]">Uploadez un plan et lancez l'extraction</p>
                <p className="text-[10.5px] mt-1" style={{ color: 'var(--c-text-faint)' }}>
                  Le résultat affichera 4 étapes : binarisation → masque → squelette → vecteurs
                </p>
              </div>
            )}
          </HudCard>
        </div>
      </div>
    </div>
  );
};

// ===== Sub-components =====

const Slider = ({ label, min, max, step, value, onChange, hint, fmt, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-[11px]" style={{ color: 'var(--c-text-2)' }}>{label}</label>
      <span className="hud-tabular text-[11px]" style={{ color: 'var(--c-gold)' }}>
        {fmt ? fmt(value) : value.toFixed(2)}
      </span>
    </div>
    <input
      type="range"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={testid}
      className="w-full"
    />
    {hint && (
      <p className="text-[10px] mt-1" style={{ color: 'var(--c-text-faint)' }}>{hint}</p>
    )}
  </div>
);

const StatPill = ({ label, value, tone }) => (
  <div className="px-2.5 py-1.5 rounded-md flex items-center justify-between"
    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
  >
    <span style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</span>
    <span className="hud-tabular font-semibold" style={{ color: tone }}>{value}</span>
  </div>
);

export default FloorplanExtractorLab;
